import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	PageHeader,
	Table,
	Card,
	Icon,
	Modal,
	Typography,
	Input,
	message,
	Button,
	Dropdown,
	Menu,
	Row,
	Col,
} from 'antd';

import Meta from '../../../../components/Meta';
import Form from '../../../../components/Form';
import Fallback from '../../../../components/Fallback';
import { Container } from './styles';

import AiAgentPersonalitiesApi from '../../../../services/sdks/ai-agent-personalities';

const { Search } = Input;

const breadcrumb = {
	routes: [
		{ breadcrumbName: 'Início' },
		{ breadcrumbName: 'Agentes de IA' },
		{ breadcrumbName: 'Personalidades' },
	],
	style: { marginBottom: 12 },
};

const AiAgentPersonalities = () => {
	const [fallback, setFallback] = useState({ initialData: true });
	const [personalities, setPersonalities] = useState([]);
	const [showUpsertModal, setShowUpsertModal] = useState(false);
	const [personality, setPersonality] = useState(null);
	const [loading, setLoading] = useState(false);

	const [pagination, setPagination] = useState({
		current: 1,
		perPage: 10,
		total: 0,
		pages: 0,
	});

	const [filters, setFilters] = useState({
		title: '',
	});

	const isFormValid = useMemo(() => {
		if (!personality?.title || !personality?.text) {
			return false;
		}
		return true;
	}, [personality]);

	const columns = [
		{
			title: 'Título',
			key: 'title',
			dataIndex: 'title',
		},
		{
			title: 'Personalidade',
			key: 'text',
			dataIndex: 'text',
			render: (text) => (text.length <= 50 ? text : `${text.slice(0, 50)}...`),
		},
		{
			title: 'Ações',
			key: 'actions',
			align: 'right',
			render: (personality) => (
				<Dropdown
					placement='bottomLeft'
					overlay={
						<Menu>
							<Menu.Item
								key='details'
								onClick={() => {
									setPersonality(personality);
									setShowUpsertModal(true);
								}}>
								<Icon type='edit' /> Editar
							</Menu.Item>

							<Menu.Divider />

							<Menu.Item
								key='delete'
								className='ant-dropdown-menu-item-danger'
								onClick={() => {
									Modal.confirm({
										title: 'Excluir Personalidade?',
										icon: 'exclamation-circle',
										content:
											'Essa ação não poderá ser revertida, realmente deseja excluir esse Personalidade?',
										okText: 'Excluir',
										onOk: () => handleDeletePersonality(personality?._id),
										okButtonProps: {
											icon: 'delete',
											type: 'danger',
										},
										cancelText: 'Cancelar',
										cancelButtonProps: {
											icon: 'close-circle',
										},
									});
								}}>
								<Icon type='delete' /> Excluir
							</Menu.Item>
						</Menu>
					}>
					<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 12 }} type='more' />
				</Dropdown>
			),
		},
	];

	const fetchInitialData = useCallback(
		async (page = 1, perPage = pagination.perPage, searchFilters = {}) => {
			try {
				setLoading(true);

				const params = { page, perPage };

				if (searchFilters.title) {
					params.title = searchFilters.title;
				}

				const response = await AiAgentPersonalitiesApi.getAllAiAgentPersonalities(params);
				const { personalities, pagination: apiPagination } = response.data;

				setPersonalities(personalities);

				setPagination({
					current: page,
					perPage: perPage,
					total: apiPagination?.total || 0,
					pages: apiPagination?.pages || 0,
				});
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os Personalidades, tente novamente');
				setPersonalities([]);
				setPagination({
					current: 1,
					perPage: pagination.perPage,
					total: 0,
					pages: 0,
				});
			} finally {
				setLoading(false);
				setFallback((prev) => ({ ...prev, initialData: false }));
			}
		},
		[pagination.perPage]
	);

	const handleDeletePersonality = useCallback(
		async (personalityId) => {
			try {
				await AiAgentPersonalitiesApi.deleteAiAgentPersonality(personalityId);

				fetchInitialData(pagination.current, pagination.perPage);

				return message.success('Personalidade excluída com sucesso');
			} catch (error) {
				console.error(error);
				message.error('Houve um erro, tente novamente');
			}
		},
		[fetchInitialData, pagination]
	);

	const handleUpsertPersonality = useCallback(async () => {
		setFallback((prev) => ({ ...prev, upsert: true }));

		try {
			const isUpdate = !!personality?._id;
			const payload = {
				title: personality.title,
				text: personality.text,
			};

			if (isUpdate) {
				await AiAgentPersonalitiesApi.updateAiAgentPersonality(personality?._id, payload);
				fetchInitialData(pagination.current, pagination.perPage, filters);
			} else {
				await AiAgentPersonalitiesApi.createAiAgentPersonality(payload);
				fetchInitialData(1, pagination.perPage, filters);
			}

			setPersonality(null);
			setShowUpsertModal(false);
			message.success('Personalidade criada/atualizada com sucesso');
		} catch (error) {
			message.error('Erro ao criar/atualizar Personalidade');
		}

		setFallback((prev) => ({ ...prev, upsert: false }));
	}, [
		fetchInitialData,
		filters,
		pagination,
		personality?._id,
		personality?.text,
		personality?.title,
	]);

	useEffect(() => {
		setPagination((prev) => ({ ...prev, perPage: 10 }));
		fetchInitialData(1, 10);
	}, [fetchInitialData]);

	const handlePageChange = useCallback(
		(page, perPage) => {
			fetchInitialData(page, perPage, filters);
		},
		[fetchInitialData, filters]
	);

	const handleSearch = useCallback(
		(value) => {
			const newFilters = { ...filters, title: value };
			setFilters(newFilters);
			fetchInitialData(1, pagination.perPage, newFilters);
		},
		[filters, pagination.perPage, fetchInitialData]
	);

	const handleClearFilters = useCallback(() => {
		const clearedFilters = { title: '' };
		setFilters(clearedFilters);
		fetchInitialData(1, pagination.perPage, clearedFilters);
	}, [pagination.perPage, fetchInitialData]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title='Personalidades dos Agentes' />

			<PageHeader
				title='Personalidades dos Agentes'
				breadcrumb={breadcrumb}
				extra={[
					<Button
						key='create-banner'
						type='ghost'
						size='large'
						icon='plus-circle'
						onClick={() => {
							setShowUpsertModal(true);
							setPersonality({});
						}}>
						Novo Personalidade
					</Button>,
				]}>
				<Typography.Text>Personalidades dos Agentes de IA adicionados pelo ADMIN</Typography.Text>
			</PageHeader>

			<Container>
				<Card style={{ marginBottom: 16 }}>
					<Row gutter={20} align='middle'>
						<Col xs={24} sm={8} md={4}>
							<Search
								placeholder='Buscar por título'
								allowClear
								onSearch={handleSearch}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} sm={8} md={4}>
							<Button icon='reload' onClick={handleClearFilters} style={{ width: '100%' }}>
								Limpar Filtros
							</Button>
						</Col>
					</Row>
				</Card>

				<Card>
					<Table
						size='middle'
						rowKey='_id'
						columns={columns}
						dataSource={personalities}
						loading={loading}
						style={{ border: 'none' }}
						pagination={{
							size: 'large',
							pageSize: pagination.perPage,
							hideOnSinglePage: true,
							current: pagination.current,
							total: pagination.total,
							onChange: handlePageChange,
							onShowSizeChange: handlePageChange,
							showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
						}}
					/>
				</Card>
			</Container>

			<Modal
				title={
					<>
						<Icon type='file-text' style={{ marginRight: 8 }} />{' '}
						{personality?._id ? 'Editar' : 'Criar'} Personalidade do Agente
					</>
				}
				width={600}
				visible={showUpsertModal}
				okButtonProps={{ disabled: !isFormValid, loading: fallback?.upsert }}
				okText={`${personality?._id ? 'Editar' : 'Criar'} Personalidade`}
				onOk={handleUpsertPersonality}
				onCancel={() => {
					setShowUpsertModal(false);
					setPersonality(null);
				}}>
				<Form.Container layout='1fr'>
					<Form.Item label='Título'>
						<Input
							placeholder='Informe o título do Personalidade'
							value={personality?.title}
							onChange={(e) => setPersonality({ ...personality, title: e.target.value })}
						/>
					</Form.Item>

					<Form.Item label='Regra do Personalidade'>
						<Input.TextArea
							placeholder='Informe a regra do Personalidade'
							value={personality?.text}
							onChange={(e) => setPersonality({ ...personality, text: e.target.value })}
							rows={18}
						/>
					</Form.Item>
				</Form.Container>
			</Modal>
		</>
	);
};

export default AiAgentPersonalities;
